/**
 * Mock GitHub Client
 * Implements IGitHubClient interface against local mock storage.
 */

import {
  ApiEnvelope,
  GitHubPullRequest,
  GitHubReviewer,
  GitHubComment,
  GitHubCheckRun,
  GitHubChangedFile,
} from '../../domain/types';
import type {
  AiReviewFinding,
  GitHubConnectionAck,
  PublishAiReviewInput,
  PublishAiReviewResult,
  PullRequestAiReview,
  AiReviewJob,
} from '@zunelo/shared';
import { ITEMS } from '../../data/initialData';
import { IGitHubClient, ClientConfig, GitHubMergeStrategy } from './types';
import {
  simulateNetwork,
  DEFAULT_CONFIG,
  isMockSourceDisconnected,
  isMockSourceRejected,
  mockRejectedCredentialMessage,
} from './mockClientUtils';
import { githubPullRequestHtmlUrl } from '@zunelo/shared';
import { isPullRequestAuthor } from '../../domain/githubMapperHelpers';

function mockPrHtmlUrl(repo: string, number: number, explicit?: string): string | undefined {
  if (explicit?.trim()) return explicit.trim();
  const [owner, name] = repo.split('/');
  if (!owner || !name) return undefined;
  return githubPullRequestHtmlUrl(owner, name, number);
}

/** Deterministic review so the demo surface exercises every severity and the publish flow. */
export function buildMockAiReview(pr: GitHubPullRequest): PullRequestAiReview {
  const firstFile = pr.changedFiles[0]?.filename ?? 'src/index.ts';
  const secondFile = pr.changedFiles[1]?.filename ?? firstFile;
  const blocking = pr.checkRuns.some((run) => run.conclusion === 'fail');
  const findings: AiReviewFinding[] = [
    {
      path: firstFile,
      line: 42,
      severity: blocking ? 'high' : 'medium',
      title: 'Estado de error no cubierto',
      detail:
        'Cuando la respuesta llega vacía el componente renderiza `undefined` en lugar del estado vacío.',
      suggestion: 'Devuelve el `EmptyState` compartido cuando `items.length === 0`.',
    },
    {
      path: secondFile,
      severity: 'low',
      title: 'Falta test del caso límite',
      detail: 'El nuevo filtro no tiene un test para la lista vacía ni para el valor `null`.',
    },
  ];
  return {
    prId: pr.id,
    headSha: pr.head.sha,
    verdict: blocking ? 'request_changes' : 'comment',
    summary: `Cambio acotado en ${pr.head.repo}: ${pr.title}. La lógica principal es correcta; hay un estado vacío sin cubrir y falta un test de borde.`,
    findings,
    testingNotes: [
      'Añade un caso con lista vacía al test del filtro.',
      'Verifica el render con `items = []` en el card.',
    ],
    filesReviewed: Math.max(1, pr.changedFiles.length),
    filesSkipped: [],
    truncated: false,
    generatedAt: '2026-09-06T10:00:00.000Z',
    provider: 'mock',
    model: 'mock-reviewer',
  };
}

export class MockGitHubClient implements IGitHubClient {
  private prs: GitHubPullRequest[];
  private config: ClientConfig;
  private aiReviews = new Map<string, PullRequestAiReview>();

  constructor(config: ClientConfig = DEFAULT_CONFIG) {
    this.config = config;

    const githubItems = ITEMS.filter((i) => i.source === 'github');
    this.prs = githubItems.map((item) => {
      const prNumber = parseInt(item.primary.replace('#', ''), 10) || 400;

      // Clone nested collections so mutating one PR's checks/reviewers/comments
      // never bleeds back into the shared fixture arrays.
      const checkRuns: GitHubCheckRun[] = (item.checks || []).map((chk, idx) => ({
        ...chk,
        id: chk.id || `chk-${prNumber}-${idx}`,
        status: chk.status || 'completed',
        conclusion: chk.conclusion || 'ok',
        durationSeconds: chk.durationSeconds || 90,
        startedAt: chk.startedAt || '2026-08-14T09:00:00.000Z',
        completedAt: chk.completedAt || '2026-08-14T09:01:30.000Z',
      }));

      const reviewers: GitHubReviewer[] = (item.reviewers || []).map((r) => ({
        ...r,
        user: { ...r.user },
      }));

      const comments: GitHubComment[] = (item.comments || []).map((c, idx) => ({
        ...c,
        id: c.id || `comm-${prNumber}-${idx}`,
        user: { ...c.user },
        diffHunk: c.diffHunk?.map((d) => ({ ...d })),
      }));

      const changedFiles: GitHubChangedFile[] = (item.files || []).map((f) => ({ ...f }));
      const repo = item.secondary.split(' · ')[0];

      return {
        id: item.id || `PR-${prNumber}`,
        number: prNumber,
        title: item.title,
        bodyLines: item.body || [item.title],
        state: item.merged ? 'merged' : 'open',
        merged: !!item.merged,
        htmlUrl: mockPrHtmlUrl(repo, prNumber, item.htmlUrl),
        head: {
          ref: item.head || 'feat/branch',
          sha: 'a1b2c3d4',
          repo,
        },
        base: {
          ref: item.base || 'main',
          sha: 'e5f6g7h8',
          repo,
        },
        author: {
          id: 1,
          login: item.role === 'author' ? 'currentUser' : 'collaborator',
          name: item.role === 'author' ? 'Tú (autor)' : 'Colaborador',
        },
        assignees: [],
        reviewers,
        comments,
        checkRuns,
        changedFiles,
        totalAdditions: item.add || 50,
        totalDeletions: item.del || 10,
        commitsCount: parseInt(item.commits?.split(' ')[0] || '3', 10),
        createdAt: '2026-08-12T10:00:00.000Z',
        updatedAt: '2026-08-14T09:45:00.000Z',
        linkedTicketKey: item.ticket,
        zuneloMeta: {
          status: item.st,
          ai: item.ai,
          next: item.next,
          refs: item.refs,
        },
      };
    });
  }

  private itemSummaries = new Map<string, string>();

  private findPR(idOrNumber: string | number): GitHubPullRequest | undefined {
    const raw = String(idOrNumber).trim();
    const cleanNum = raw.replace(/^PR-/, '').replace(/^#/, '');
    return this.prs.find(
      (p) =>
        p.id === raw ||
        p.id === `PR-${cleanNum}` ||
        `#${p.number}` === raw ||
        String(p.number) === cleanNum,
    );
  }

  async getPullRequests(): Promise<ApiEnvelope<GitHubPullRequest[]>> {
    if (isMockSourceDisconnected('github') || isMockSourceRejected('github')) {
      return simulateNetwork(() => [], this.config);
    }
    return simulateNetwork(() => [...this.prs], this.config);
  }

  async getPullRequest(
    idOrNumber: string | number,
  ): Promise<ApiEnvelope<GitHubPullRequest | null>> {
    return simulateNetwork(() => {
      const match = this.findPR(idOrNumber);
      return match ? { ...match } : null;
    }, this.config);
  }

  async toggleCommentResolution(
    prId: string,
    commentIndex: number,
    resolved?: boolean,
  ): Promise<ApiEnvelope<GitHubPullRequest>> {
    return simulateNetwork(() => {
      const pr = this.findPR(prId);
      if (!pr || !pr.comments[commentIndex]) {
        throw new Error(`Comment at index ${commentIndex} not found for PR ${prId}`);
      }
      const comment = pr.comments[commentIndex];
      if (comment.state !== 'Aprobado') {
        comment.resolved = typeof resolved === 'boolean' ? resolved : !comment.resolved;
        comment.state = comment.resolved ? 'Resuelto' : 'Sin resolver';
        pr.updatedAt = new Date().toISOString();
      }
      return { ...pr };
    }, this.config);
  }

  async retryCheckRun(prId: string, checkIndex: number): Promise<ApiEnvelope<GitHubPullRequest>> {
    return simulateNetwork(() => {
      const pr = this.findPR(prId);
      if (!pr || !pr.checkRuns[checkIndex]) {
        throw new Error(`Check run at index ${checkIndex} not found for PR ${prId}`);
      }
      pr.checkRuns[checkIndex].conclusion = 'ok';
      pr.checkRuns[checkIndex].status = 'completed';
      pr.checkRuns[checkIndex].title = 'Completado con éxito tras reintento';
      pr.updatedAt = new Date().toISOString();
      return { ...pr };
    }, this.config);
  }

  async reRunAllChecks(prId: string): Promise<ApiEnvelope<GitHubPullRequest>> {
    return simulateNetwork(() => {
      const pr = this.findPR(prId);
      if (!pr) {
        throw new Error(`PR ${prId} not found`);
      }
      pr.checkRuns = pr.checkRuns.map((chk) => ({
        ...chk,
        conclusion: 'ok',
        status: 'completed',
        title: `${chk.name} completado con éxito (re-ejecutado)`,
      }));
      pr.updatedAt = new Date().toISOString();
      return { ...pr };
    }, this.config);
  }

  async approvePullRequest(prId: string): Promise<ApiEnvelope<GitHubPullRequest>> {
    return simulateNetwork(() => {
      const pr = this.findPR(prId);
      if (!pr) {
        throw new Error(`PR ${prId} not found`);
      }
      const currentUserReviewer = pr.reviewers.find((r) => r.isCurrentUser);
      if (currentUserReviewer) {
        currentUserReviewer.state = 'Aprobado';
        currentUserReviewer.reviewedAt = new Date().toISOString();
      } else if (pr.reviewers.length > 0) {
        const targetReviewer = pr.reviewers.find((r) => r.state !== 'Aprobado') || pr.reviewers[0];
        targetReviewer.state = 'Aprobado';
        targetReviewer.isCurrentUser = true;
        targetReviewer.reviewedAt = new Date().toISOString();
      } else {
        pr.reviewers.push({
          user: {
            id: 1,
            login: 'currentUser',
            name: 'Tú',
            initials: 'TU',
            color: 'var(--z-accent)',
          },
          state: 'Aprobado',
          isCurrentUser: true,
          reviewedAt: new Date().toISOString(),
        });
      }
      pr.updatedAt = new Date().toISOString();
      return { ...pr };
    }, this.config);
  }

  async mergePullRequest(
    prId: string,
    _commitMessage?: string,
    mergeStrategy: GitHubMergeStrategy = 'squash',
  ): Promise<ApiEnvelope<GitHubPullRequest>> {
    return simulateNetwork(() => {
      const pr = this.findPR(prId);
      if (!pr) {
        throw new Error(`PR ${prId} not found`);
      }
      pr.merged = true;
      pr.state = 'merged';
      pr.lastMergeStrategy = mergeStrategy;
      pr.updatedAt = new Date().toISOString();
      return { ...pr };
    }, this.config);
  }

  async generateItemSummary(
    prId: string,
    options?: { force?: boolean },
  ): Promise<ApiEnvelope<{ summary: string; refreshed?: boolean }>> {
    return simulateNetwork(() => {
      const pr = this.findPR(prId);
      if (!pr) {
        throw new Error(`PR ${prId} not found`);
      }
      const force = options?.force === true;
      const cached = this.itemSummaries.get(pr.id);
      if (cached && !force) {
        return { summary: cached, refreshed: false };
      }
      const summary =
        pr.bodyLines && pr.bodyLines.length > 0
          ? `${pr.title}. ${pr.bodyLines[0]}`
          : `${pr.title}: cambio en ${pr.head.repo} sin descripción adicional.`;
      this.itemSummaries.set(pr.id, summary);
      pr.zuneloMeta = {
        status: pr.zuneloMeta?.status ?? 'you',
        next: pr.zuneloMeta?.next ?? 'Revisar el PR',
        tags: pr.zuneloMeta?.tags,
        refs: pr.zuneloMeta?.refs,
        ai: summary,
      };
      return { summary, refreshed: force };
    }, this.config);
  }

  async generateAiReview(
    prId: string,
    options?: { force?: boolean },
  ): Promise<ApiEnvelope<AiReviewJob | PullRequestAiReview>> {
    return simulateNetwork(() => {
      const pr = this.findPr(prId);
      if (!pr) throw new Error(`PR ${prId} not found`);
      const cached = this.aiReviews.get(pr.id);
      if (cached && !options?.force) {
        return { ...cached, cached: true };
      }
      const review = buildMockAiReview(pr);
      this.aiReviews.set(pr.id, review);
      return review;
    }, this.config);
  }

  async getAiReviewJob(jobId: string): Promise<ApiEnvelope<AiReviewJob>> {
    const prId = jobId.replace(/^mock-/, '');
    const review = this.aiReviews.get(prId);
    if (!review) throw new Error('Revisión no encontrada');
    return {
      serverTime: new Date().toISOString(),
      data: {
        jobId,
        prId,
        status: 'completed',
        stage: 'finalizing',
        progressMessage: 'Revisión terminada.',
        review,
        createdAt: review.generatedAt,
        updatedAt: review.generatedAt,
      },
    };
  }

  async listAiReviewJobs(
    options: { prId?: string; activeOnly?: boolean } = {},
  ): Promise<ApiEnvelope<AiReviewJob[]>> {
    const jobIds = [...this.aiReviews.entries()]
      .filter(([prId]) => !options.prId || prId === options.prId)
      .map(([prId, review]) => ({
        jobId: `mock-${prId}`,
        prId,
        status: 'completed' as const,
        stage: 'finalizing' as const,
        progressMessage: 'Revisión terminada.',
        review,
        createdAt: review.generatedAt,
        updatedAt: review.generatedAt,
      }));
    return { serverTime: new Date().toISOString(), data: options.activeOnly ? [] : jobIds };
  }

  async cancelAiReviewJob(jobId: string): Promise<ApiEnvelope<AiReviewJob>> {
    return this.getAiReviewJob(jobId);
  }

  async publishAiReview(
    prId: string,
    input: PublishAiReviewInput,
  ): Promise<ApiEnvelope<PublishAiReviewResult>> {
    return simulateNetwork(() => {
      const pr = this.findPr(prId);
      if (!pr) throw new Error(`PR ${prId} not found`);
      const isAuthor = isPullRequestAuthor(pr);
      const event = isAuthor ? 'COMMENT' : input.event;
      const inline = input.review.findings.filter((f) => f.line && f.path !== '(general)').length;
      return {
        reviewUrl: `${pr.htmlUrl ?? ''}#pullrequestreview-mock`,
        inlineComments: inline,
        event,
      };
    }, this.config);
  }

  private findPr(prId: string): GitHubPullRequest | undefined {
    return this.prs.find(
      (p) =>
        p.id === prId ||
        p.id === `PR-${prId}` ||
        String(p.number) === String(prId).replace(/^PR-/, ''),
    );
  }

  async getConnection(): Promise<ApiEnvelope<GitHubConnectionAck>> {
    return simulateNetwork(() => buildMockGithubConnectionAck(), this.config);
  }

  async testConnection(): Promise<ApiEnvelope<GitHubConnectionAck>> {
    if (isMockSourceRejected('github')) {
      return simulateNetwork(
        () => ({
          status: 'error' as const,
          login: null,
          checkedAt: new Date().toISOString(),
          error: mockRejectedCredentialMessage('github'),
          code: 'GITHUB_TOKEN_INVALID',
        }),
        this.config,
      );
    }
    return this.getConnection();
  }

  async postConversationComment(prId: string, markdown: string): Promise<ApiEnvelope<unknown>> {
    return simulateNetwork(() => {
      const pr = this.findPr(prId);
      if (!pr) throw new Error(`PR ${prId} not found`);
      if (!markdown.trim()) throw new Error('Comment cannot be empty');
      return {
        id: `mock-comment-${pr.number}`,
        body: markdown,
        html_url: `${pr.htmlUrl ?? ''}#issuecomment-mock`,
      };
    }, this.config);
  }

  async generateConversationCommentDraft(
    prId: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return simulateNetwork(() => {
      const pr = this.findPr(prId);
      if (!pr) throw new Error(`PR ${prId} not found`);
      const idea = hint?.trim();
      const draft = idea
        ? `${idea}\n\nRef: ${pr.title}`
        : `Sobre ${pr.title}: ¿podemos confirmar el estado de este PR antes de continuar?`;
      return { draft, provider: 'deterministic' };
    }, this.config);
  }
}

function buildMockGithubConnectionAck(): GitHubConnectionAck {
  const checkedAt = new Date().toISOString();
  if (isMockSourceDisconnected('github')) {
    return {
      status: 'not_configured',
      login: null,
      checkedAt,
      error: null,
      code: 'GITHUB_TOKEN_MISSING',
    };
  }
  return {
    status: 'mock',
    login: null,
    checkedAt,
    error: null,
    code: null,
  };
}
